import Layout from "@/HOC";
import Head from "next/head";
import React from "react";
import { useRouter } from "next/router";
import { useFormik } from "formik";
import * as Yup from "yup";
import { DeleteOutlined } from "@ant-design/icons";
import styles from "./data-deletion.module.css";
import { deleteAccount } from "@/utils/apis/data-deletion/data-deletion.api";
import { errorToast, successToast } from "@/component/toasts";
import { tokens } from "@/utils/jwtToken";
import DefaultLoader from "@/component/loader";
import DefaultSeo from "@/component/seo/DefaultSeo";

const validation = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Email is required"),
  reason: Yup.string(),
});

const getErrorMessage = (error: any) =>
  error?.response?.data?.message || "Something went wrong. Please try again.";

function DataDeletion() {
  const router = useRouter();

  const formik = useFormik({
    initialValues: { email: "", reason: "", honeyPot: "" },
    validationSchema: validation,
    onSubmit: async (values, { resetForm }) => {
      try {
        const res = await deleteAccount(values);
        if (res.data.success) {
          resetForm();
          successToast("Your account has been deleted successfully.", {
            autoClose: 4000,
          });
          tokens.removeAccessToken();
          tokens.removeRefreshToken();
          setTimeout(() => router.push("/home"), 2500);
        }
      } catch (error) {
        errorToast(getErrorMessage(error));
      }
    },
  });

  return (
    <div>
      <DefaultSeo
        title="Data Deletion Request"
        description="You have the right to request permanent deletion of your personal data. Review the policy below and submit a request."
      />
      <Head>
        <meta name="robots" content="noindex" />
      </Head>
      <Layout>
        <section className={styles.page}>
          <div className={styles.hero}>
            <span className={styles.badge}>
              <span className={styles.dot} />
              ACCOUNT MANAGEMENT
            </span>
            <h1 className={styles.title}>Data Deletion Request</h1>
            <p className={styles.subtitle}>
              You have the right to request permanent deletion of your personal
              data.
              <br />{" "}
              Review the policy below and submit a request.
            </p>
            <p className={styles.updated}>
              Policy last updated: <strong>July 2026</strong>
            </p>
          </div>

          <form onSubmit={formik.handleSubmit} className={styles.card} noValidate>
            <input type="text" hidden {...formik.getFieldProps("honeyPot")} />
            <div className={styles.cardHead}>
              <span className={styles.iconWrap}>
                <DeleteOutlined />
              </span>
              <div>
                <h2>Delete Account</h2>
                <p>Your data will be permanently removed</p>
              </div>
            </div>

            <div className={styles.cardBody}>
              <label htmlFor="email" className={styles.label}>
                Email Address
              </label>
              <input
                id="email"
                type="email"
                placeholder="Enter your registered email"
                className={styles.input}
                {...formik.getFieldProps("email")}
              />
              {formik.touched.email && formik.errors.email && (
                <p className={styles.error}>{formik.errors.email}</p>
              )}

              <label htmlFor="reason" className={styles.label}>
                Reason for Deletion
              </label>
              <textarea
                id="reason"
                rows={4}
                placeholder="Tell us why you want to leave..."
                className={styles.textarea}
                {...formik.getFieldProps("reason")}
              />

              <button
                type="submit"
                className={styles.submit}
                disabled={formik.isSubmitting}
              >
                {formik.isSubmitting ? <DefaultLoader /> : "Submit Request"}
              </button>
              <p className={styles.note}>
                By submitting, you confirm that you understand this action is{" "}
                <span className={styles.danger}>irreversible</span>.
              </p>
            </div>
          </form>
        </section>
      </Layout>
    </div>
  );
}

export default DataDeletion;
